/**
 * Navigation Equipment Helpers
 * Pure predicates for navigation equipment requirements
 * ANCHOR MODE compliant
 */

import { 
  isSeagoing,
  isCat, isCatAtLeast, isCatAtMost,
  getTotalPersons, getGrossTonnage
} from './index.js';

// Helper functions for legacy category checks
const isCategory0 = (v) => isCat(v, 0);
const isCategory1 = (v) => isCat(v, 1);
const isCategory2 = (v) => isCat(v, 2);
const isCategory3 = (v) => isCat(v, 3);
const isCategory4 = (v) => isCat(v, 4);
const isCategory5 = (v) => isCat(v, 5);
const isCategory6 = (v) => isCat(v, 6);
const isCategory0to1 = (v) => isCatAtMost(v, 1);
const isCategory0to2 = (v) => isCatAtMost(v, 2);
const isCategory3to4 = (v) => isCat(v, 3) || isCat(v, 4);

// MF/HF Radio fitted check
export const isMFFitted = (v) =>
  v?.gmdss?.mf === true ||
  v?.radios?.mf_hf === true ||
  v?.equipment?.radio?.mf === true ||
  v?.hasMFHF === true ||
  v?.hasMF === true ||
  false;

// ===== COMPASS REQUIREMENTS =====

export const requiresCompass = (vessel) => true; // All vessels per WBC3 19.2.1

export const requiresCompassLight = (vessel) => {
  // Categories 0-5 require compass lighting per WBC3 19.2.7
  return !isCategory6(vessel);
};

export const requiresBearingDevice = (vessel) => {
  // Bearing taking device required for seagoing vessels
  return isSeagoing(vessel);
};

export const isSteelVessel = (vessel) => {
  return vessel?.construction === 'steel' || vessel?.hullMaterial === 'steel';
};

// ===== CHART REQUIREMENTS =====

export const requiresCharts = (vessel) => {
  // All vessels except Category 6 only per WBC3 19.3.1
  return !isCategory6(vessel);
};

export const isCategory6Only = (vessel) => {
  return vessel?.category === 6;
};

// ===== ELECTRONIC NAVIGATION =====

export const requiresGPS = (vessel) => {
  // Area 0-4 require GPS per WBC3 19.4.1
  return isCategory0to2(vessel) || isCategory3to4(vessel);
};

export const requiresTwoGPS = (vessel) => {
  // Area 0-2 require two GPS units
  return isCategory0to2(vessel);
};

export const requiresAIS = (vessel) => {
  // Required if ≥300GT or carries passengers per WBC3 19.7.1
  const tonnage = getGrossTonnage(vessel);
  return tonnage >= 300 || (getTotalPersons(vessel) > 0 && isSeagoing(vessel));
};

export const requiresRadar = (vessel) => {
  // Required for Area 0-2 operations per WBC3 19.5.1
  return isCategory0to2(vessel);
};

export const requiresEchoSounder = (vessel) => {
  // Required for Area 0-4 per WBC3 19.6.1
  return isCategory0to2(vessel) || isCategory3to4(vessel);
};

export const requiresECDIS = (vessel) => {
  // ECDIS typically required for larger SOLAS vessels
  return isSOLASLSA(vessel) && getGrossTonnage(vessel) >= 500;
};

// ===== RADIO COMMUNICATIONS =====

export const requiresVHF = (vessel) => {
  // VHF required for most commercial operations
  return isSeagoing(vessel) || vessel?.operatesCommercially;
};

export const requiresMFHF = (vessel) => {
  // MF/HF required for Area 0-1 operations per WBC3 17.2.1  
  return isCategory0to1(vessel);
};

export const requiresGMDSS = (vessel) => {
  // GMDSS required for SOLAS vessels
  return isSOLASLSA(vessel);
};

// ===== SIGNALLING EQUIPMENT =====

export const requiresSignalLamp = (vessel) => {
  // Signalling lamp required for larger vessels
  return isSeagoing(vessel) && (getGrossTonnage(vessel) >= 12 || isCategory0to2(vessel));
};

export const requiresSoundSignals = (vessel) => {
  // Sound signal appliances required per COLREGS
  return vessel?.length >= 12; // 12m+ vessels need sound signals
};

export const requiresShapes = (vessel) => {
  // Day shapes required for vessels that may need to display them
  return isSeagoing(vessel) && vessel?.operatesCommercially;
};

// ===== EQUIPMENT FITTING CHECKS =====

export const isCompassFitted = (vessel) => {
  return vessel?.hasCompass !== false; // Default true
};

export const isGPSFitted = (vessel) => {
  return vessel?.hasGPS || vessel?.navigation?.includes('gps');
};

export const isRadarFitted = (vessel) => {
  return vessel?.hasRadar || vessel?.navigation?.includes('radar');
};

export const isAISFitted = (vessel) => {
  return vessel?.hasAIS || vessel?.navigation?.includes('ais');
};

export const isEchoSounderFitted = (vessel) => {
  return vessel?.hasEchoSounder || vessel?.navigation?.includes('echo_sounder');
};

export const isVHFFitted = (vessel) => {
  return vessel?.hasVHF || vessel?.radio?.includes('vhf');
};

export const isMFHFFitted = (vessel) => {
  return vessel?.hasMFHF || vessel?.radio?.includes('mf_hf');
};

// ===== SOLAS NAVIGATION =====

export const isSOLASNavigation = (vessel) => {
  // Vessels subject to SOLAS navigation requirements
  return isSOLASLSA(vessel);
};

export const requiresGyroCompass = (vessel) => {
  // Gyro compass required for vessels ≥300GT
  return getGrossTonnage(vessel) >= 300;
};

export const requiresSecondRadar = (vessel) => {
  // Second radar required for vessels ≥300GT
  return getGrossTonnage(vessel) >= 300;
};

export const requiresARPA = (vessel) => {
  // ARPA required for vessels ≥300GT
  return getGrossTonnage(vessel) >= 300;
};

// ===== SPECIAL VESSEL TYPES =====

export const isPilotVessel = (vessel) => {
  return vessel?.vesselType === 'pilot';
};

export const isHighSpeedCraft = (vessel) => {
  return vessel?.vesselType === 'hsc' || vessel?.isHighSpeedCraft;
};

export const isPassengerVessel = (vessel) => {
  return getTotalPersons(vessel) > 0;
};

// ===== CATEGORY HELPERS =====

// Robust category helpers that handle strings/numbers
const asNum = (x) => (typeof x === 'string' ? parseInt(x.replace(/\D/g,''),10) : x);

export const getCategory = (v) => asNum(v?.category ?? v?.areaCategory ?? null);

// Use isCat from core.js instead (imported at top)
// export const isCat = (v, cat) => getCategory(v) === asNum(cat);

// Use isCatAtMost and isCatAtLeast from core.js instead
// export const isCatAtMost = (v, max) => {
//   const c = getCategory(v); 
//   return typeof c === 'number' && c <= asNum(max);
// };

// export const isCatAtLeast = (v, min) => {
//   const c = getCategory(v); 
//   return typeof c === 'number' && c >= asNum(min);
// };

// ===== ADDITIONAL EQUIPMENT CHECKS =====

export const hasPaperCharts = (vessel) => {
  return vessel?.hasCharts !== false && !vessel?.hasECDISOnly;
};

export const hasECDIS = (vessel) => {
  return vessel?.hasECDIS || vessel?.navigation?.includes('ecdis');
};

export const hasNavElectronics = (vessel) => {
  return isGPSFitted(vessel) || isRadarFitted(vessel) || isAISFitted(vessel) || isEchoSounderFitted(vessel);
};

// NAVTEX check
export const isNavtexFitted = (vessel) => {
  return vessel?.hasNavtex === true || 
         vessel?.navigation?.includes('navtex') || 
         vessel?.radio?.includes('navtex');
};

// Legacy aliases for migration compatibility
export const isNavLightSystemFitted = (vessel) => vessel?.hasNavLights !== false;
export const isSignallingSystemFitted = (vessel) => vessel?.hasSignalling !== false;